var SessionsFormFieldManager = class {
  /*
      Types of conflicts:  1. Time conflict within same session -> Disable other time buttons
                           2. Time conflict with other session  -> Disable other time session time buttons
                           3. Time conflict with one or more providers in another session -> Won't know until provider chosen

      Time conflict Assumptions:  1. startTime and endTime are from the chosen time slot.
                                  2. timeButtonStartTime is the time for each button being checked for conflict. timeButtonEndTime is start + duration.
                                  3. NOTE: Only disable conflicting times if a provider is selected. Only re-enable conflicts when a provider is unselected.
                                  4. A provider is automatically selected if there is only one for the time selected.
                                  5. A provider is unselected if the associated time button is unselected.
                                  6. Start Time Conflict:
                                     If ((timeButtonStartTime >= startTime) && (timeButtonStartTime < endTime)) then there is a conflict for the start.
                                  7. Overlap Conflict (start is before chosen time slot start, but it continues into the duration of the chosen slot):
                                      If (timeButtonStartTime + shortest provider slot duration for that session) is within the chosen time slot then there is also a conflict.
                                      Assumption here is that if there are multiple providers at least the shortest one could potentially conflict.
                                            Visually that looks like this (dashed line represents longer provider session):
                                                      Chosen Slot                   Start |===================| End
                                                      Conflict Session    Start |============|---------| End
                                      Any provider slot that has a duration that would overlap a chosen time also needs to be marked as disabled.
                                      Logic: If ((timeButtonStarttime <= startTime) && (timeButtonEndTime > startTime)) then there is a conflict overlap.

      Test cases:  1. User selects Time:
                      a. Other times in same session disabled.
                      b. Unselect any times in other sessions that have no provider selected, since these are incomplete and may have the same time selected as the current choice.
                         --> Need to check newly enabled sessions' times for possible conflicts with already selected sessions and mark conflicts (disable time buttons).
                      c. Disable any provider in the selected session which has a conflict with already selected sessions.
                      d. Show providers. If only 1 provider then select that too - goto #2.
                   2. User selects Provider:
                      a. Other providers in same session disabled.
                      b. Disable conflicting times in other sessions that are not already selected. (Selected means a provider has been selected for the session.)
                   3. User unselects Time:
                      a. Other times in same session enabled.
                      b. If a provider was selected for that time:
                        Enable times in other sessions that were conflicts with this one!
                        --> #1. Find other sessions that have not been selected - those are the only ones we need to deal with.
                            #2. Re-Enable any marked conflict time buttons for the reenabled provider slot in unselected sessions.
                   4. User unselects Provider:
                     a. Other providers in the same session are enabled.
                     b. Same as 3b above (but 'if' conditional does not apply as provider slot was previously selected):
                        Enable times in other sessions that were conflicts with this one.
                        --> #1. Find sessions that have not been selected.
                            #2. Re-Enable any marked conflict time buttons for the reenabled provider slot in unselected sessions.

*/

  constructor(container) {
    container.decorated = true;

    this.container = jQuery(container);
    this.input = this.container.find("[data-behaviour=slot-input]");

    this.disableEmptySessions();

    // BEHAVIOUR
    this.container.on("click", "[data-behaviour~=time-selection]:not(.disabled)", this.handleSelectTime.bind(this));
    this.container.on("click", "[data-behaviour~=slot-selection]:not(.disabled)", this.handleSelectSlot.bind(this));
  }

  // EVENT HANDLERS

  handleSelectTime(e) {
    var timeButton = jQuery(e.currentTarget);
    this.enableUnselectedSessions(timeButton); // Enable any times that have no provider selected
    this.updateSessionTimeButtons(timeButton); // Enable/Disable other start time buttons in the same session
    this.updateSessionSlotButtons(timeButton); // Reveal/Hide slot buttons for the selected start time (if only 1 provider, select it)

    if (timeButton.hasClass("selected")) {
      // if a timeButton was selected rather than unselected...
      this.checkSlotButtonForOverlap(timeButton); //   then check if any providers for the selected timeButton have a conflict
    }

    this.save();
  }

  handleSelectSlot(e) {
    var slotButton = jQuery(e.currentTarget);
    let previousSelection = slotButton.closest(".provider-list").find(".slot-button.selected");
    if (previousSelection.length > 0) {
      // if there was a slot selected before...
      this.updateConflictingSlotsByDuration(previousSelection, false); // then first clear any conflicting times for the now unselected button
    }

    this.updateSessionTimeSlotButtons(slotButton); // Reveal/Hide other provider buttons in the same session
    if (slotButton.hasClass("selected")) {
      this.updateConflictingSlotsByDuration(slotButton, true); // Enable/Disable start time buttons in other sessions that conflict with this selected provider
    }
    this.save();
  }

  // HELPERS

  // Enables or disables the selected session start time and make sure at most one time button is marked selected (does not affect other sessions)
  updateSessionTimeButtons(timeButton) {
    var timesContainer = timeButton.closest(".time-list");
    // Clear any conflicts if a provider slot is unselected
    let previousSelectedSlot = timeButton
      .closest(".session-slot-selector")
      .find(".provider-list .slot-button.selected");
    if (previousSelectedSlot.length) {
      this.updateConflictingSlotsByDuration(previousSelectedSlot, false); // Clear any conflicting slots for the now unselected button
    }

    if (timeButton.hasClass("selected")) {
      // User unselected time in session so remove selection classes and remove conflicts
      this.unselectAllSessionTimeSlotButtons(timeButton); // Make all session providers in that same session un-selected
      timesContainer.removeClass("time-selected");
      timeButton.removeClass("selected icon icon-checkmark");
    } else {
      // User selected a time button
      var previousSelection = timesContainer.find(".time-button.selected");
      if (previousSelection.length) {
        previousSelection.removeClass("selected icon icon-checkmark");
      }
      timeButton.addClass("selected icon icon-checkmark");
      timesContainer.addClass("time-selected");
    }
  }

  // Reveal the appropriate slot button container for the selected time
  updateSessionSlotButtons(timeButton) {
    var sessionContainer = timeButton.closest(".session-slot-selector");

    sessionContainer.find(".provider-list").removeClass("selected"); // clear any provider container selection
    sessionContainer.find(".provider-list").removeClass("slot-selected"); // clear any provider container selection
    sessionContainer.find(".slot-button").removeClass("selected icon icon-checkmark"); // clear any provider button selection
    if (timeButton.hasClass("selected")) {
      var selectedTimeSlotList = sessionContainer.find('.provider-list[data-time="' + timeButton.data("time") + '"]');
      selectedTimeSlotList.addClass("selected");

      // We want our parents to know that a slot has been selected so that they can possibly scroll down to show the new list
      selectedTimeSlotList.trigger("sessions:time-slot-selected", [timeButton]);

      // If there is only one slot do not give users a choice and instead select the slot for them
      this.checkIfOnlyOneSlot(selectedTimeSlotList);
    }
  }

  // Check if there is only one slot and do not give users a choice and instead select the slot for them
  checkIfOnlyOneSlot(selectedTimeSlotList) {
    if (selectedTimeSlotList.find(".slot-button").length == 1) {
      let onlySlot = selectedTimeSlotList.find(".slot-button").first();
      onlySlot.addClass("slot-button--only-provider disabled");
      this.updateSessionTimeSlotButtons(onlySlot);
      this.updateConflictingSlotsByDuration(onlySlot, true); // Check and disable any conflicts in other sessions
    }
  }

  // Disable(default) or enable any other session start times where the session lies within the duration of the selected provider
  updateConflictingSlotsByDuration(slotButton, enableConflict = true) {
    // Get elements with other time slots to be checked
    var otherSessionTimeSlots = slotButton.closest(".sessions-input").find(".time-list").not(".time-selected").get();
    // Is slot provider selected or unselected?
    if (slotButton.closest(".provider-list").has(".slot-button.selected")) {
      // get start time from provider
      var startTime = new Date(slotButton.data("startTime")); // convert from UNIX time format
      var endTime = new Date(slotButton.data("endTime"));

      // Loop through and check each slot for overlap
      otherSessionTimeSlots.forEach((timeList) =>
        this.checkAllTimeSlotsForOverlap(timeList, startTime, endTime, enableConflict),
      );
    }
    // else slot/provider was unselected just now, so re-enable other providers in same session
    else {
      this.updateSessionTimeSlotButtons(slotButton); // Enable other providers in same session
      // clear time conflicts in other sessions
      jQuery(otherSessionTimeSlots).find(".time-button").removeClass("disabled");
    }
  }

  checkAllTimeSlotsForOverlap(timeList, startTime, endTime, enableConflict) {
    let timeButtons = jQuery(timeList).find(".time-button").get();
    timeButtons.forEach((timeButton) => this.checkTimeSlotForOverlap(timeButton, startTime, endTime, enableConflict));
  }

  // Check actual time-slot for overlap and disable or enable as necessary
  checkTimeSlotForOverlap(timeButton, startTime, endTime, enableConflict) {
    this.startTime = startTime;
    this.endTime = endTime;
    let $timeButton = jQuery(timeButton);
    let time = $timeButton.data("time");
    let overlappingSlots = this.checkOtherProviderSlots($timeButton, time);

    if (overlappingSlots) {
      $timeButton.toggleClass("disabled", enableConflict);
    }
  }

  // Review Provider slots under each time, to confirm overlap or not on the clicked button
  //
  // returns bool
  checkOtherProviderSlots(timeButton, time) {
    let slotButtonsList = timeButton
      .parents(".session-slot-selector")
      .find('.provider-list[data-time="' + time + '"] .slot-button');
    let overlap = false;

    slotButtonsList.each((ind, slot) => {
      let slotStartTime = new Date(jQuery(slot).data("startTime"));
      let slotEndTime = new Date(jQuery(slot).data("endTime"));
      if (
        (slotStartTime >= this.startTime && slotStartTime < this.endTime) ||
        (slotStartTime <= this.startTime && slotEndTime > this.startTime)
      ) {
        overlap = true;
      }
    });
    return overlap;
  }

  // Disable any provider slot buttons that have durations that conflict with already selected sessions - Only consider providers for the selected slot!
  checkSlotButtonForOverlap(timeButton) {
    let slotButtonsList = jQuery(timeButton)
      .closest(".session-slot-selector")
      .find(".provider-list.selected .slot-button")
      .get();
    slotButtonsList.forEach((slot) => {
      let selectedSlots = this.selectedSlots();
      jQuery(selectedSlots)
        .get()
        .forEach((selectedSlot) => {
          let selectedStartTime = new Date(jQuery(selectedSlot).data("startTime"));
          let selectedEndTime = new Date(jQuery(selectedSlot).data("endTime"));
          let startTime = new Date(jQuery(slot).data("startTime"));
          let endTime = new Date(jQuery(slot).data("endTime"));
          if (
            (startTime >= selectedStartTime && startTime < selectedEndTime) ||
            (startTime <= selectedStartTime && endTime > selectedStartTime)
          ) {
            jQuery(slot).toggleClass("disabled", true);
          }
        });
    });
  }

  // Select or unselect the clicked slot and make sure at most one slot button is marked selected
  updateSessionTimeSlotButtons(slotButton) {
    var slotsContainer = slotButton.closest(".provider-list");
    if (slotButton.hasClass("selected")) {
      slotsContainer.removeClass("slot-selected");
      slotButton.removeClass("selected icon icon-checkmark");
    } else {
      slotsContainer.find(".slot-button").removeClass("selected icon icon-checkmark");
      slotButton.addClass("selected icon icon-checkmark");
      slotsContainer.addClass("slot-selected");
    }
  }

  // Clear all selections and re-enable all providers in the same session (Called when user unselects a start time button)
  unselectAllSessionTimeSlotButtons(timeButton) {
    timeButton.closest(".sesssion-slot-selector").find(".slot-button").removeClass("selected icon icon-checkmark");
    timeButton.closest(".sesssion-slot-selector").find(".slot-button").removeClass("disabled");
    timeButton.closest(".sesssion-slot-selector").find(".provider-list").removeClass("selected");
    timeButton.closest(".sesssion-slot-selector").find(".provider-list").removeClass("slot-selected");
  }

  // Get unselected sessions and enable all times
  enableUnselectedSessions(timeButton) {
    var otherUnselectedSessions = timeButton
      .closest(".sessions-input")
      .find(".session-slot-selector")
      .not(jQuery(".session-slot-selector .provider-list.slot-selected").closest(".session-slot-selector"));
    otherUnselectedSessions
      .find(".time-list")
      .not(jQuery(timeButton).closest(".time-list"))
      .removeClass("time-selected");
    otherUnselectedSessions.find(".time-button").not(timeButton).removeClass("selected icon icon-checkmark");
    otherUnselectedSessions.find(".provider-list").removeClass("selected");
  }

  // When sessions have no slots they should be not be openable.
  disableEmptySessions() {
    this.container.find(".expandable-section").each(shouldBeDisabled);
    function shouldBeDisabled(i, element) {
      if (!jQuery(element).find(".provider-list").length) {
        jQuery(element).find(".expandable-title").addClass("expandable-disabled");
        jQuery(element).find(".expandable-title .no-slots").show();
      }
    }
  }

  // Update the hidden input with the selected slot ids and preview data
  save() {
    this.input.val(JSON.stringify(this.slotIds()));
    this.input.data("preview", this.slotPreviewData());
  }

  slotIds() {
    return this.selectedSlots()
      .map(function () {
        return jQuery(this).data("slot-id");
      })
      .get();
  }

  slotPreviewData() {
    return this.selectedSlots()
      .map(function () {
        return jQuery(this).data("slot-preview");
      })
      .get();
  }

  selectedSlots() {
    return this.container.find(".slot-button.selected");
  }
};

SessionsFormFieldManager.decorateAll = function () {
  jQuery("[data-behaviour=sessions-input]").each(function () {
    if (!this.decorated) {
      new SessionsFormFieldManager(this);
    }
  });
};

SessionsFormFieldManager.decorateAll();

window.SessionsFormFieldManager = SessionsFormFieldManager;
